// src/components/WebsiteDownloads.js
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

/* ───────────────── helpers: date, csv, pagination ───────────────── */

function toISODate(d) {
  const pad = (n) => String(n).padStart(2, '0');
  const y = d.getFullYear();
  const m = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  return `${y}-${m}-${day}`;
}
function toUtcISOStringFromLocalDate(localDateStr, endOfDay = false) {
  if (!localDateStr) return '';
  const d = new Date(localDateStr + (endOfDay ? 'T23:59:59' : 'T00:00:00'));
  // convert local to UTC ISO
  return new Date(
    d.getTime() - d.getTimezoneOffset() * 60000
  ).toISOString();
}

function downloadCsv({ headers, rows, filename }) {
  const esc = (v) => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const headerLine = headers.map(h => esc(h.label)).join(',');
  const lines = rows.map(row =>
    headers.map(h => esc(h.get ? h.get(row) : row[h.key])).join(',')
  );
  const csv = [headerLine, ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function usePageSlice(items, page, pageSize) {
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  return items.slice(start, end);
}

function Pagination({ page, pageSize, total, onChange }) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const canPrev = page > 1;
  const canNext = page < pageCount;
  const bar = { display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', padding: 12, borderTop: '1px solid #e5e7eb', background: '#fff' };
  const btn = (enabled) => ({
    padding: '6px 10px', border: '1px solid #e5e7eb', background: enabled ? '#fff' : '#f3f4f6',
    borderRadius: 8, cursor: enabled ? 'pointer' : 'not-allowed', fontSize: 12
  });
  const meter = { fontSize: 12, color: '#64748b' };
  const select = { padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12 };

  return (
    <div style={bar}>
      <span style={meter}>
        Page {page} / {pageCount} • {total.toLocaleString()} rows
      </span>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <select
          value={pageSize}
          onChange={(e)=>onChange({ page: 1, pageSize: Number(e.target.value) })}
          style={select}
          title="Rows per page"
        >
          {[10,25,50,100,250,500].map(n => <option key={n} value={n}>{n} / page</option>)}
        </select>
        <button onClick={()=>onChange({ page: 1 })} disabled={!canPrev} style={btn(canPrev)} title="First">«</button>
        <button onClick={()=>onChange({ page: page - 1 })} disabled={!canPrev} style={btn(canPrev)} title="Prev">‹</button>
        <button onClick={()=>onChange({ page: page + 1 })} disabled={!canNext} style={btn(canNext)} title="Next">›</button>
        <button onClick={()=>onChange({ page: pageCount })} disabled={!canNext} style={btn(canNext)} title="Last">»</button>
      </div>
    </div>
  );
}

/* ───────────────── component ───────────────── */

export default function WebsiteDownloads() {
  // filters
  const [startDate, setStartDate] = useState(toISODate(new Date(Date.now() - 7*24*3600*1000)));
  const [endDate, setEndDate] = useState(toISODate(new Date()));
  const [emailFilter, setEmailFilter] = useState('');

  // data
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // reset page on filter changes
  useEffect(() => { setPage(1); }, [startDate, endDate, emailFilter]);

  // fetch
  useEffect(() => {
    let cancelled = false;
    const startDateUtc = toUtcISOStringFromLocalDate(startDate, false);
    const endDateUtc = toUtcISOStringFromLocalDate(endDate, true);

    async function fetchRecords() {
      setLoading(true);
      setErr('');
      try {
        const res = await axios.get(`${API_BASE_URL}/api/getemailrecords`, {
          params: { StartDateUtc: startDateUtc, EndDateUtc: endDateUtc },
        });
        const raw = Array.isArray(res.data) ? res.data : (res.data?.items ?? []);
        if (!cancelled) setRecords(raw);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.message || e.message || 'Failed to load email records');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchRecords();
    return () => { cancelled = true; };
  }, [startDate, endDate]);

  // normalize to { email, dateAddedUtc }
  const normalized = useMemo(() => {
    return (records || []).map(r => {
      const email = r.email || r.Email || r.userEmail || r.UserEmail || r.address || '';
      // common keys for date-ish fields
      const dateVal =
        r.dateAddedUtc || r.DateAddedUtc || r.date || r.createdAt || r.created ||
        r.timestamp || r.addedAt || r.Date || r.TimeStamp || '';
      const iso = dateVal ? new Date(dateVal).toISOString() : '';
      return { email, dateAddedUtc: iso, _raw: r };
    }).filter(x => x.email || x.dateAddedUtc);
  }, [records]);

  // filter + sort
  const filtered = useMemo(() => {
    const s = startDate ? new Date(startDate + 'T00:00:00') : new Date('2000-01-01T00:00:00');
    const e = endDate ? new Date(endDate + 'T23:59:59') : new Date('2999-12-31T23:59:59');

    return normalized.filter(r => {
      if (emailFilter && !(r.email || '').toLowerCase().includes(emailFilter.toLowerCase())) return false;
      const t = r.dateAddedUtc ? new Date(r.dateAddedUtc) : null;
      if (!t) return false;
      return t >= s && t <= e;
    });
  }, [normalized, emailFilter, startDate, endDate]);

  const sorted = useMemo(() => {
    const c = [...filtered];
    c.sort((a, b) => {
      const da = a.dateAddedUtc ? new Date(a.dateAddedUtc).getTime() : 0;
      const db = b.dateAddedUtc ? new Date(b.dateAddedUtc).getTime() : 0;
      return db - da; // newest first
    });
    return c;
  }, [filtered]);

  const currentPageRows = usePageSlice(sorted, page, pageSize);

  // csv
  const csvHeaders = [
    { key: 'email', label: 'Email' },
    { key: 'dateAddedUtc', label: 'Date Added (Local)', get: r => r.dateAddedUtc ? new Date(r.dateAddedUtc).toLocaleString() : '' },
  ];
  const exportCurrent = () =>
    downloadCsv({ headers: csvHeaders, rows: currentPageRows, filename: `website-downloads_page-${page}.csv` });
  const exportAll = () =>
    downloadCsv({ headers: csvHeaders, rows: sorted, filename: `website-downloads_all.csv` });

  // ui styles
  const shell = { background: '#f8fafc', minHeight: '100%', padding: 16 };
  const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' };
  const titleRow = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 12, borderBottom: '1px solid #e5e7eb' };
  const filterBar = { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: 12, borderBottom: '1px solid #e5e7eb' };
  const input = { padding: '8px 10px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12, outline: 'none' };
  const btnBase = { padding: '8px 12px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer' };
  const btnGhost = { ...btnBase, background: '#fff', color: '#0f172a', border: '1px solid #e5e7eb' };
  const btnPrimary = { ...btnBase, background: '#0b0f19', color: '#fff', border: '1px solid #0b0f19' };
  const tableWrap = { overflowX: 'auto' };
  const table = { width: '100%', borderCollapse: 'collapse', fontSize: 12 };
  const th = { background: '#f6f7fb', textAlign: 'left', padding: 10, fontWeight: 700, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
  const td = { padding: 10, borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' };

  const headers = [
    { key: 'email', label: 'Email' },
    { key: 'dateAddedUtc', label: 'Date Added' },
  ];

  return (
    <div style={shell}>
      <div style={card}>
        {/* Title + CSV */}
        <div style={titleRow}>
          <h2 style={{ fontSize: 16, margin: 0 }}>Website Downloads</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={exportCurrent} style={btnGhost}>Export current page</button>
            <button onClick={exportAll} style={btnPrimary}>Export all (filtered)</button>
          </div>
        </div>

        {/* Filters */}
        <div style={filterBar}>
          <label style={{ fontSize: 12, color: '#64748b' }}>
            Start
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ ...input, marginLeft: 6 }}
            />
          </label>
          <label style={{ fontSize: 12, color: '#64748b' }}>
            End
            <input
              type="date"
              value={endDate}
              max={toISODate(new Date())}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ ...input, marginLeft: 6 }}
            />
          </label>
          <label style={{ fontSize: 12, color: '#64748b' }}>
            Email
            <input
              type="text"
              placeholder="user@company.com"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              style={{ ...input, marginLeft: 6, minWidth: 220 }}
            />
          </label>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => {
                // forces refetch via date states
                setStartDate(startDate);
              }}
              style={btnGhost}
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={() => {
                setEmailFilter('');
                setStartDate(toISODate(new Date(Date.now() - 7*24*3600*1000)));
                setEndDate(toISODate(new Date()));
              }}
              style={btnGhost}
            >
              Clear
            </button>
          </div>
        </div>

        {/* Table */}
        <div style={tableWrap}>
          <table style={table}>
            <thead>
              <tr>
                {headers.map(h => (<th key={h.key} style={th}>{h.label}</th>))}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={headers.length} style={{ padding: 16, color: '#666' }}>
                    {loading ? 'Loading…' : 'No records found.'}
                  </td>
                </tr>
              ) : (
                currentPageRows.map((r, i) => (
                  <tr key={`${r.email}-${r.dateAddedUtc || i}`}>
                    <td style={{ ...td, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.email || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {r.dateAddedUtc ? new Date(r.dateAddedUtc).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          page={page}
          pageSize={pageSize}
          total={sorted.length}
          onChange={({ page: p = page, pageSize: ps = pageSize }) => { setPage(p); setPageSize(ps); }}
        />
      </div>

      {/* Errors */}
      {err && (
        <div style={{ marginTop: 12, color: '#b91c1c', fontSize: 13, background: '#fef2f2', border: '1px solid #fecaca', padding: 10, borderRadius: 8 }}>
          {err}
        </div>
      )}

      {/* Debug: show raw when mapping misses email */}
      {!loading && !err && normalized.length > 0 && normalized.some(r => !r.email) && (
        <details style={{ marginTop: 16 }}>
          <summary style={{ cursor: 'pointer' }}>Some rows didn’t map — view raw</summary>
          <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: 12, borderRadius: 8, overflowX: 'auto' }}>
            {JSON.stringify(records, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}
